import type { RuntimeConfig } from '../config';
import { IdentityError, type IdentityService } from './IdentityService';

const requestTimeoutMs = 8_000;

export interface StationUser {
  userId: string;
  displayName: string;
  muted?: boolean;
}

export interface StationMessage {
  id: number;
  userId: string;
  displayName: string;
  body: string;
  createdAt: string;
}

export interface StationSnapshot {
  // Optional while a newly published Pages bundle can still reach the prior
  // API revision during a rolling backend deployment.
  available?: boolean;
  messages: StationMessage[];
  online: StationUser[];
  muted: StationUser[];
  cursor: number;
}

export class StationService {
  public constructor(private readonly config: RuntimeConfig, private readonly identity: IdentityService) {}

  public snapshot(after = 0, signal?: AbortSignal): Promise<StationSnapshot> {
    const cursor = after > 0 ? '?after=' + String(after) : '';
    return this.request('/api/v1/station' + cursor, { signal });
  }

  public send(body: string): Promise<StationMessage> {
    return this.request('/api/v1/station/messages', { method: 'POST', body: JSON.stringify({ body }) });
  }

  public report(messageId: number): Promise<void> {
    return this.request('/api/v1/station/messages/' + encodeURIComponent(String(messageId)) + '/reports', { method: 'POST' });
  }

  public setMuted(userId: string, muted: boolean): Promise<void> {
    return this.request('/api/v1/station/mutes/' + encodeURIComponent(userId), { method: muted ? 'PUT' : 'DELETE' });
  }

  public leave(keepalive = false): Promise<void> {
    return this.request('/api/v1/station/presence', { method: 'DELETE', keepalive });
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const authorization = this.identity.authorization();
    if (!authorization) throw new IdentityError('Log in to enter the Station.', 'account_required');
    const controller = new AbortController();
    const externalSignal = init.signal;
    const abortFromCaller = (): void => controller.abort();
    if (externalSignal?.aborted) controller.abort();
    else externalSignal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = window.setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      const response = await fetch(this.config.apiOrigin + path, {
        ...init,
        headers: { 'Content-Type': 'application/json', Authorization: authorization, ...init.headers },
        signal: controller.signal,
      });
      this.requireCurrentAuthorization(authorization);
      if (response.status === 204) return undefined as T;
      const payload = await response.json() as T & { error?: { code?: string; message?: string } };
      this.requireCurrentAuthorization(authorization);
      if (!response.ok) {
        throw new IdentityError(payload.error?.message ?? 'The Station channel failed.', payload.error?.code ?? 'request_failed');
      }
      return payload;
    } catch (error) {
      if (error instanceof IdentityError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new IdentityError('The Station channel did not answer in time.', 'timeout');
      }
      throw new IdentityError('Cannot reach the Station channel.', 'offline');
    } finally {
      window.clearTimeout(timer);
      externalSignal?.removeEventListener('abort', abortFromCaller);
    }
  }

  private requireCurrentAuthorization(authorization: string): void {
    if (this.identity.authorization() !== authorization) {
      throw new IdentityError('The account session changed. Reopen the Station channel.', 'session_changed');
    }
  }
}
